import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft } from "lucide-react";
import { adminApi, apiError } from "@/admin/adminApi";
import { PageHead } from "@/admin/AdminLayout";
import ProgramForm, { EMPTY_PROGRAM, toForm, toPayload } from "@/admin/components/ProgramForm";
import SessionsPanel from "@/admin/components/SessionsPanel";
import { btnPrimary, card } from "@/admin/ui";

export default function AdminProgramEdit() {
  const { id } = useParams();
  const isNew = id === "uj";
  const navigate = useNavigate();
  const [form, setForm] = useState(isNew ? EMPTY_PROGRAM : null);
  const [sessions, setSessions] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isNew) return;
    adminApi
      .get(`/admin/programs/${id}`)
      .then(({ data }) => {
        setForm(toForm(data));
        setSessions(data.sessions);
      })
      .catch((e) => toast.error(apiError(e)));
  }, [id, isNew]);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (isNew) {
        const { data } = await adminApi.post("/admin/programs", toPayload(form));
        toast.success("Az új program elmentve.");
        navigate(`/admin/programok/${data.id}`, { replace: true });
      } else {
        const { data } = await adminApi.put(`/admin/programs/${id}`, toPayload(form));
        setForm(toForm(data));
        toast.success("A program módosításai elmentve.");
      }
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSaving(false);
    }
  };

  if (!form) return <p className="font-body text-graphite-soft">Betöltés…</p>;

  return (
    <div data-testid="admin-program-edit-page">
      <Link to="/admin/programok" data-testid="admin-program-back" className="inline-flex items-center gap-2 font-body text-[13px] text-graphite-soft hover:text-gold mb-5">
        <ArrowLeft className="w-4 h-4" /> Vissza a programokhoz
      </Link>
      <PageHead eyebrow={isNew ? "Új program" : form.number} title={isNew ? "Új program létrehozása" : form.title || "Program"} />
      <form onSubmit={save} className={`${card} px-6 md:px-8 py-7`} data-testid="program-form">
        <ProgramForm form={form} setForm={setForm} />
        <div className="flex justify-end pt-7 mt-7 border-t border-hairline">
          <button type="submit" disabled={saving} data-testid="program-form-save" className={btnPrimary}>
            {saving ? "Mentés…" : isNew ? "Program létrehozása" : "Módosítások mentése"}
          </button>
        </div>
      </form>
      <div className="mt-8">
        {isNew ? (
          <p className="font-body text-[14px] text-graphite-soft" data-testid="sessions-after-save-note">Időpontokat a program első mentése után adhatsz hozzá.</p>
        ) : (
          <SessionsPanel programId={id} sessions={sessions} setSessions={setSessions} />
        )}
      </div>
    </div>
  );
}
